import { Flex, Layout, Menu } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useRecoilState, useSetRecoilState } from 'recoil'
import Swal from 'sweetalert2'
import { getUserRole } from '../../api/aops.api'
import loginApi from '../../api/login.api'
import { isAopsChiefState, menuSelectedTeam } from '../../store/aops'
import { menuState } from '../../store/menu'
import { tokenState } from '../../store/token'
import { userAuthStatus, userState } from '../../store/user'
import '../../styles/layout.scss'
import PageRouting from './PageRouting'

const { Header, Sider, Content } = Layout

const PageLayout = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [menu, setMenu] = useRecoilState(menuState)
  const [user, setUser] = useRecoilState(userState)
  const [, setToken] = useRecoilState(tokenState)
  const [openKey, setOpenKey] = useState([])
  const [selectKey, setSelectKey] = useState([])
  const [, setAuthStatus] = useRecoilState(userAuthStatus)
  const [isAopsChief] = useRecoilState(isAopsChiefState)
  const setTeam = useSetRecoilState(menuSelectedTeam)

  const [userRole, setUserRole] = useState(null)
  useEffect(() => {
    const fetchUserRole = async () => {
      if (user?.id) {
        const res = await getUserRole(user.id)
        setUserRole(res)
      }
    }
    fetchUserRole()
  }, [user?.id])
  const isEmployeeLayout = userRole === 'employee'

  // 인사 관리 메뉴 URL 정의
  const hrMenuUrls = {
    chief: ['/hr/org-chart', '/hr/annual-leave'], // 관리자용: 조직도, 연차 관리
    employee: ['/hr/my-hr-manage'], // 일반 직원용: 내 인사 관리
  }

  const menuItems = useMemo(
    () =>
      menu
        ?.map((menuItem) => {
          // 판매관리-선물하기, 문의/리뷰관리-상품문의 관리 임시 가리기
          const excludedMenuIds = [
            'order_gift',
            'product_inquiry_list',
            'meal_category_create', // temp
          ]

          let filteredSubMenuList = menuItem.subMenuList?.filter(
            (subMenu) => !excludedMenuIds.includes(subMenu.unique_id),
          )

          // 인사 관리 메뉴 필터링
          if (isAopsChief) {
            // 관리자: 조직도, 연차 관리만 보여주기
            filteredSubMenuList = filteredSubMenuList?.filter((subMenu) => {
              if (subMenu.url && subMenu.url.startsWith('/hr/')) {
                return hrMenuUrls.chief.includes(subMenu.url)
              }
              return true // 인사 관리가 아닌 메뉴는 모두 표시
            })
          } else {
            // 일반 직원: 내 인사 관리만 보여주기
            filteredSubMenuList = filteredSubMenuList?.filter((subMenu) => {
              if (subMenu.url && subMenu.url.startsWith('/hr/')) {
                return hrMenuUrls.employee.includes(subMenu.url)
              }
              return true // 인사 관리가 아닌 메뉴는 모두 표시
            })
          }

          // 하위 메뉴가 없는 메뉴는 제외
          if (!filteredSubMenuList || filteredSubMenuList.length === 0) {
            return null
          }

          return {
            key: menuItem.unique_id,
            label: menuItem.name,
            children: filteredSubMenuList?.map((subMenu) => {
              const label =
                subMenu.url === '/program/manage'
                  ? '프로그램 상세 관리'
                  : subMenu.name

              return {
                key: subMenu.url || subMenu.unique_id,
                label,
              }
            }),
          }
        })
        .filter(Boolean) || [], // null 제거
    [menu, isAopsChief],
  )

  useEffect(() => {
    if (pathname !== '/') {
      const findMenu = menu?.find(
        (mainMenu) =>
          mainMenu.subMenuList?.findIndex(
            (subMenu) => subMenu.url === pathname,
          ) > -1,
      )
      if (findMenu) {
        setOpenKey([pathname, findMenu?.unique_id])
        setSelectKey([pathname])
      } else {
        setOpenKey([])
        setSelectKey([])
      }
    } else {
      setOpenKey([])
      setSelectKey([])
    }
  }, [pathname])

  const handleMenuOpenChange = (openKeys) => {
    const currentOpenKey = openKeys.find((key) => openKey.indexOf(key) === -1)
    if (currentOpenKey !== undefined) {
      setOpenKey([currentOpenKey])
    } else {
      setOpenKey(openKeys)
    }
  }

  const handleMenuClick = ({ key }) => {
    if (key.includes('/')) {
      navigate(key)
      // console.log('key::', key)
      if (key.includes('/schedule/')) {
        setTeam(key.split('/')[2])
      }
      // 파라미터 추가
    }
  }

  const handleLogout = () => {
    Swal.fire({
      customClass: { popup: 'custom-alert' },
      text: '로그아웃하시겠습니까?',
      confirmButtonText: '확인',
      confirmButtonColor: '#117FFA',
      showCancelButton: true,
      cancelButtonText: '취소',
      cancelButtonColor: '#FFFFFF',
    }).then(async (result) => {
      if (result.isConfirmed) {
        // 로그아웃 처리
        let msg = '로그아웃에 실패하였습니다. 다시 시도해주세요.'
        const param = {
          id: user?.id,
        }
        const res = await loginApi.logout(param)
        if (res.data.code === 200) {
          msg = '정상적으로 로그아웃 되었습니다.'
        }
        Swal.fire({
          customClass: { popup: 'custom-alert' },
          text: msg,
          confirmButtonText: '확인',
          confirmButtonColor: '#117FFA',
        }).then(() => {
          if (res.data.code === 200) {
            setToken({
              accessToken: null,
              refreshToken: null,
            })
            setUser({
              id: 0,
              name: '',
              employeeId: null,
              teamGroupId: null,
              studioGroupId: null,
            })
            setAuthStatus({
              studioName: '',
              studioNameKo: '',
              teamName: '',
              teamNameKo: '',
            })
            setMenu([])
            navigate('/')
          }
        })
      }
    })
  }

  return isEmployeeLayout ? (
    <Layout className={'layout layout-aops'}>
      <Header className={'aops-header'}>
        {/*로고 - AOPS*/}
        <Flex
          align={'center'}
          gap={8}
          className={'logo-wrap'}
          onClick={() => navigate('/')}
        >
          <img
            src={`${process.env.REACT_APP_HOST}/assets/aops-logo.png`}
            style={{ width: '25px', height: '25px' }}
          />
        </Flex>
        <Flex align={'center'} gap={8}>
          {isAopsChief && (
            <div type="default" disabled={true} className="admin-mode-chip">
              관리자 Mode
            </div>
          )}
          <p className={'user-name'}>{user?.name} 님</p>
          <p className={'header-separator'} />
          <p onClick={handleLogout} className={'logout'}>
            로그아웃
          </p>
        </Flex>
      </Header>
      <Layout>
        <Sider width={240} className={'aops-sider'}>
          <div
            style={{ padding: '24px', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <img
              src={`${process.env.REACT_APP_HOST}/assets/aops-logo.png`}
              style={{ width: '48px', height: '48px' }}
            />
          </div>
          <Menu
            className={'aops-menu-wrap'}
            mode="inline"
            items={menuItems}
            openKeys={openKey}
            onOpenChange={handleMenuOpenChange}
            selectedKeys={selectKey}
            onClick={handleMenuClick}
            inlineIndent={12}
          />
        </Sider>
        <Content className={'aops-content'}>
          <PageRouting />
        </Content>
      </Layout>
    </Layout>
  ) : (
    // 일반 레이아웃: 헤더가 위에 존재
    <Layout className={'layout'}>
      <Header className={'header'}>
        {/*로고 - 슈가코치 SALUS*/}
        <Flex
          align={'center'}
          gap={8}
          className={'logo-wrap'}
          onClick={() => navigate('/')}
        >
          <img
            src={`${process.env.REACT_APP_HOST}/assets/sugar-logo.png`}
            className={'logo-img'}
          />
          <p className={'logo-title'}>
            슈가코치 <span>SALUS</span>
          </p>
        </Flex>
        <Flex align={'center'} gap={15}>
          <p className={'user-name'}>{user?.name} 님</p>
          <p onClick={handleLogout} className={'logout'}>
            로그아웃
          </p>
        </Flex>
      </Header>
      <Layout>
        <Sider width={200}>
          <Menu
            className={'menu-wrap'}
            mode="inline"
            items={menuItems}
            openKeys={openKey}
            onOpenChange={handleMenuOpenChange}
            selectedKeys={selectKey}
            onClick={handleMenuClick}
            inlineIndent={12}
          />
        </Sider>
        <Content>
          <PageRouting />
        </Content>
      </Layout>
    </Layout>
  )
}

export default PageLayout
